/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { SelectField, SelectFieldOption, SelectFieldProps } from '@squiz/sds';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { MONITORING_STRINGS } from '../../../../Constants/Strings.constants';
import { LogsQuery } from '../../Logs.constants';
import { PRESET_TIMES, PRESET_TIME_CUSTOM, SELECT_PRESET_TIME_DATA_IDS } from './SelectPresetTime.constants';

/**
 * Select field for preset time ranges to use when filter logs.
 */
export const SelectPresetTime = (): JSX.Element => {
  const { t } = useTranslation();

  // URL search params
  const [searchParams, setSearchParams] = useSearchParams();

  const ref = useRef<HTMLInputElement>(null);

  // Selected value
  const [presetTime, setPresetTime] = useState<string>(searchParams.get(LogsQuery.date.range) as string);

  // Labels
  const labels = useMemo(() => {
    return {
      fieldLabel: t(MONITORING_STRINGS.selectTimeRange),
    };
  }, [t]);

  const onSelect = (option: SelectFieldOption): void => {
    setPresetTime(option.value);
  };

  useEffect(() => {
    if (presetTime) {
      searchParams.set(LogsQuery.date.range, presetTime);

      // Remove custom date parameters when using presets
      if (presetTime !== PRESET_TIME_CUSTOM) {
        searchParams.delete(LogsQuery.date.from);
        searchParams.delete(LogsQuery.date.to);
      }

      setSearchParams(searchParams);
    }
  }, [presetTime, searchParams, setSearchParams]);

  const getSelectFieldOptions = (): Array<SelectFieldOption> => {
    const timeOptions = PRESET_TIMES.map((time) => {
      const { amount, unit } = time;

      const suffix = amount > 1 ? 's' : '';
      const label = `Last ${amount} ${unit}${suffix}`;

      return {
        dataId: SELECT_PRESET_TIME_DATA_IDS.selectOption,
        key: `${amount}+${unit}`,
        label,
        value: `${amount}+${unit}`,
      };
    });

    return [
      {
        dataId: SELECT_PRESET_TIME_DATA_IDS.selectOption,
        key: PRESET_TIME_CUSTOM,
        label: 'Custom',
        value: PRESET_TIME_CUSTOM,
      },
      ...timeOptions,
    ];
  };

  const options = getSelectFieldOptions();

  const selectFieldProps: SelectFieldProps = {
    callback: onSelect,
    defaultOption: options[1],
    fieldLabel: labels.fieldLabel,
    hideLabel: true,
    id: `preset-time`,
    name: `preset-time`,
    options,
    value: presetTime || ``,
  };

  return <SelectField {...selectFieldProps} data-id={SELECT_PRESET_TIME_DATA_IDS.selectField} ref={ref} />;
};
